import { Coins, ShieldAlert, ShoppingBag, Star, type LucideIcon } from 'lucide-react';
import type { Locale } from '@/lib/i18n';

export type NotificationTypeKey =
  | 'ORDER_PLACED'
  | 'ORDER_DELIVERED'
  | 'ORDER_CANCELLED'
  | 'DISPUTE_OPENED'
  | 'DISPUTE_RESOLVED'
  | 'REVIEW_RECEIVED'
  | 'DEPOSIT_SETTLED';

export interface NotificationItem {
  id: string;
  type: NotificationTypeKey;
  data: Record<string, unknown> | null;
  readAt: string | null;
  emailSentAt?: string | null;
  createdAt: string;
}

/** Route a notification opens when clicked (translated title/body live client-side). */
export function notificationRoute(type: NotificationTypeKey): string {
  if (type === 'DEPOSIT_SETTLED') return '/dashboard/wallet';
  return '/dashboard/orders';
}

export function notificationIcon(type: NotificationTypeKey): LucideIcon {
  switch (type) {
    case 'REVIEW_RECEIVED':
      return Star;
    case 'DEPOSIT_SETTLED':
      return Coins;
    case 'DISPUTE_OPENED':
    case 'DISPUTE_RESOLVED':
      return ShieldAlert;
    default:
      return ShoppingBag;
  }
}

/** Replace `{key}` placeholders in a translation template with data values. */
export function interpolate(
  template: string,
  data: Record<string, unknown> | null,
): string {
  if (!data) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = data[key];
    return value == null ? match : String(value);
  });
}

const rtfCache = new Map<Locale, Intl.RelativeTimeFormat>();

/** Memoized relative time (e.g. "5 minutes ago") keyed by active locale. */
export function formatRelativeTime(iso: string, locale: Locale): string {
  let rtf = rtfCache.get(locale);
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    rtfCache.set(locale, rtf);
  }
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs)) return '';
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 1) return rtf.format(0, 'minute');
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return rtf.format(-days, 'day');
  const months = Math.round(days / 30);
  return rtf.format(-months, 'month');
}